/**
 * Payment utility functions for consistent fee calculations across the application
 */

/**
 * Calculates CC processing fee using Stripe's pass-through formula.
 * 
 * Formula: totalToCharge = (base + fixedFee) / (1 - percentageFee)
 * Fee = totalToCharge - base
 * 
 * This ensures the merchant receives exactly the base amount after Stripe
 * deducts their 2.9% + $0.30 fee.
 * 
 * Example with $1,000 base (100,000 cents):
 * - Total to charge: (100,000 + 30) / (1 - 0.029) = 100,030 / 0.971 = 103,018 cents
 * - Stripe takes: ($1,030.18 × 2.9%) + $0.30 = $29.87 + $0.30 = $30.17
 * - Merchant receives: $1,030.18 - $30.17 = $1,000.01 ≈ $1,000 ✓
 * 
 * @param baseAmountCents - The base amount in cents
 * @param ccFeePercentage - The percentage fee (default: 2.9)
 * @param fixedFeeCents - The fixed fee in cents (default: 30)
 * @returns The processing fee in cents to add to the base amount
 */
export function calculateCcProcessingFee(
  baseAmountCents: number, 
  ccFeePercentage: number = 2.9, 
  fixedFeeCents: number = 30
): number {
  if (baseAmountCents <= 0) return 0;
  
  const totalToChargeCents = Math.ceil(
    (baseAmountCents + fixedFeeCents) / (1 - ccFeePercentage / 100)
  );
  return totalToChargeCents - baseAmountCents;
}
